import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useToast } from '../hooks/useToast';

type DomainStatus = 'none' | 'pending_validation' | 'provisioning' | 'active' | 'removing' | 'error';

interface DomainState {
  domainStatus: DomainStatus;
  customDomain?: string;
  acmValidationCname?: { name: string; value: string } | null;
  cloudfrontDomainName?: string;
  message?: string;
}

export default function DomainSettings() {
  const { siteId } = useParams<{ siteId: string }>();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [state, setState] = useState<DomainState>({ domainStatus: 'none' });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await api.getDomainStatus(siteId);
      setState(data as DomainState);
    } catch (err) {
      console.error('Failed to fetch domain status:', err);
    }
  }, [siteId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchStatus();
      setLoading(false);
    })();
  }, [fetchStatus]);

  useEffect(() => {
    const shouldPoll = ['pending_validation', 'provisioning', 'removing'].includes(state.domainStatus);
    if (shouldPoll) {
      pollRef.current = setInterval(fetchStatus, 15000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [state.domainStatus, fetchStatus]);

  const handleSetDomain = async () => {
    if (!siteId || !domainInput.trim()) return;
    const domain = domainInput.trim().toLowerCase();
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(domain)) {
      addToast('error', 'That doesn\'t look like a valid domain. Try something like www.mysite.com');
      return;
    }
    setSaving(true);
    try {
      await api.setDomain(siteId, domain);
      addToast('success', 'Got it! Now we need to verify you own that domain.');
      await fetchStatus();
    } catch (err: any) {
      addToast('error', err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckStatus = async () => {
    setSaving(true);
    try {
      await fetchStatus();
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!siteId) return;
    if (!window.confirm('Remove your custom domain? Your site will still be available at its topzite.net address.')) return;
    setSaving(true);
    try {
      await api.removeCustomDomain(siteId);
      addToast('success', 'Domain removal started');
      await fetchStatus();
    } catch (err: any) {
      addToast('error', err.message || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const handleTryAgain = async () => {
    if (!siteId) return;
    setSaving(true);
    try {
      await api.removeCustomDomain(siteId);
      await fetchStatus();
      setDomainInput('');
    } catch (err: any) {
      addToast('error', err.message || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      addToast('success', 'Copied!');
    }).catch(() => {
      addToast('error', 'Couldn\'t copy — try selecting and copying manually');
    });
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1><svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg> Your Own Domain</h1></div>
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header"><h1><svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg> Your Own Domain</h1></div>

      {/* ───── No domain ───── */}
      {state.domainStatus === 'none' && (
        <div className="card">
          <h2 style={{ marginBottom: '0.5rem' }}>Use your own web address</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            Right now your site lives at <strong>yourname.topzite.net</strong>. If you've bought your own domain
            (like <strong>www.myshop.com</strong>), you can connect it here so visitors go straight to your site.
          </p>

          <p style={{ color: '#666', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            Don't have a domain yet? You can buy one from{' '}
            <a href="https://www.namecheap.com" target="_blank" rel="noopener noreferrer">Namecheap</a>,{' '}
            <a href="https://www.cloudflare.com/products/registrar/" target="_blank" rel="noopener noreferrer">Cloudflare</a>,{' '}
            <a href="https://www.godaddy.com" target="_blank" rel="noopener noreferrer">GoDaddy</a>, or any domain provider.
            Prices start around $10-15/year.
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', maxWidth: '500px' }}>
            <input
              type="text"
              className="input"
              placeholder="www.mysite.com"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSetDomain()}
              disabled={saving}
              style={{ flex: 1 }}
            />
            <button
              className="btn btn-primary"
              onClick={handleSetDomain}
              disabled={saving || !domainInput.trim()}
            >
              {saving ? 'Setting up...' : 'Connect Domain'}
            </button>
          </div>

          <p style={{ color: '#999', fontSize: '0.85rem', marginTop: '1rem' }}>
            Use the <strong>www</strong> version of your domain (e.g., <strong>www.mysite.com</strong>). This works with all providers.
          </p>
        </div>
      )}

      {/* ───── Pending validation ───── */}
      {state.domainStatus === 'pending_validation' && (
        <div className="card">
          <h2 style={{ marginBottom: '0.5rem' }}>Step 1 of 3 — Prove it's your domain</h2>
          <p style={{ color: '#666', marginBottom: '1rem', lineHeight: 1.6 }}>
            We need to check that <strong>{state.customDomain}</strong> actually belongs to you. To do this, you'll
            add a small record in your domain settings. This doesn't change anything about your site — it's just a verification step.
          </p>

          {state.acmValidationCname ? (
            <>
              <div style={{
                background: '#f0f7ff',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1rem',
                border: '1px solid #bfdbfe',
              }}>
                <p style={{ fontWeight: 600, marginBottom: '1rem', color: '#1e40af' }}>
                  Here's what to do:
                </p>
                <ol style={{ margin: 0, paddingLeft: '1.25rem', color: '#444', lineHeight: 2, fontSize: '0.95rem' }}>
                  <li>Log in to wherever you bought your domain (GoDaddy, Namecheap, Cloudflare, etc.)</li>
                  <li>Find the <strong>DNS settings</strong> or <strong>DNS records</strong> section</li>
                  <li>Add a new record with these settings:</li>
                </ol>

                <div style={{
                  background: '#fff',
                  borderRadius: '6px',
                  padding: '1rem',
                  marginTop: '1rem',
                  border: '1px solid #e0e0e0',
                }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Type</span>
                    <span style={{ fontSize: '0.95rem' }}>CNAME</span>
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Name</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <code style={{ fontSize: '0.85rem', wordBreak: 'break-all', flex: 1, background: '#f8f9fa', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                        {state.acmValidationCname.name}
                      </code>
                      <button className="btn btn-secondary" onClick={() => copyToClipboard(state.acmValidationCname!.name)} style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}>
                        Copy
                      </button>
                    </div>
                  </div>
                  <div>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Value</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <code style={{ fontSize: '0.85rem', wordBreak: 'break-all', flex: 1, background: '#f8f9fa', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                        {state.acmValidationCname.value}
                      </code>
                      <button className="btn btn-secondary" onClick={() => copyToClipboard(state.acmValidationCname!.value)} style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}>
                        Copy
                      </button>
                    </div>
                  </div>
                </div>

                <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#666', lineHeight: 1.5 }}>
                  <strong>Not sure how?</strong> Search for "<em>how to add a CNAME record</em>" plus your provider's name
                  (e.g., "how to add a CNAME record GoDaddy"). There are guides for every provider.
                </p>
              </div>

              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                Once you've added it, come back here and we'll detect it automatically. It usually takes a few minutes.
              </p>
            </>
          ) : (
            <p style={{ color: '#999', fontStyle: 'italic', marginBottom: '1rem' }}>
              Getting your verification details ready...
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button className="btn btn-primary" onClick={handleCheckStatus} disabled={saving}>
              {saving ? 'Checking...' : 'Check Now'}
            </button>
            <button className="btn btn-secondary" onClick={handleRemoveDomain} disabled={saving} style={{ color: '#dc3545' }}>
              Cancel
            </button>
          </div>

          <p style={{ color: '#999', fontSize: '0.85rem', marginTop: '0.75rem' }}>
            We're also checking automatically every 15 seconds.
          </p>
        </div>
      )}

      {/* ───── Provisioning ───── */}
      {state.domainStatus === 'provisioning' && (
        <div className="card">
          <h2 style={{ marginBottom: '0.5rem' }}>Step 2 of 3 — Setting things up</h2>
          <p style={{ color: '#666', marginBottom: '1rem', lineHeight: 1.6 }}>
            Verification passed! We're now setting up secure hosting for <strong>{state.customDomain}</strong>.
            This includes an SSL certificate (the padlock icon in browsers) and a fast content delivery network.
          </p>

          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner" />
            <p style={{ marginTop: '1rem', color: '#666', fontWeight: 500 }}>This usually takes 5-15 minutes</p>
            <p style={{ color: '#999', fontSize: '0.85rem' }}>You can close this page and come back later — it'll keep going.</p>
          </div>

          <button className="btn btn-primary" onClick={handleCheckStatus} disabled={saving}>
            {saving ? 'Checking...' : 'Check Now'}
          </button>
        </div>
      )}

      {/* ───── Active ───── */}
      {state.domainStatus === 'active' && (
        <div className="card">
          <h2 style={{ marginBottom: '0.5rem' }}>Step 3 of 3 — Connect your domain</h2>
          <p style={{ color: '#666', marginBottom: '1rem', lineHeight: 1.6 }}>
            Almost there! The secure hosting for <strong>{state.customDomain}</strong> is ready.
            Now you just need to point your domain to it.
          </p>

          {state.cloudfrontDomainName && (
            <div style={{
              background: '#f0f7ff',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '1rem',
              border: '1px solid #bfdbfe',
            }}>
              <p style={{ fontWeight: 600, marginBottom: '1rem', color: '#1e40af' }}>
                Go back to your domain provider and add (or update) this record:
              </p>

              <div style={{
                background: '#fff',
                borderRadius: '6px',
                padding: '1rem',
                border: '1px solid #e0e0e0',
                marginBottom: '1rem',
              }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Type</span>
                  <span style={{ fontSize: '0.95rem' }}>CNAME</span>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Name</span>
                  <span style={{ fontSize: '0.95rem' }}>{state.customDomain?.startsWith('www.') ? 'www' : state.customDomain}</span>
                </div>
                <div>
                  <span style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Value</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <code style={{ fontSize: '0.85rem', wordBreak: 'break-all', flex: 1, background: '#f8f9fa', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                      {state.cloudfrontDomainName}
                    </code>
                    <button className="btn btn-secondary" onClick={() => copyToClipboard(state.cloudfrontDomainName!)} style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}>
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              <p style={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.5 }}>
                If you already have a record for <strong>{state.customDomain?.startsWith('www.') ? 'www' : state.customDomain}</strong>,
                update its value to the one above. If you're using Cloudflare, make sure the orange cloud is <strong>turned off</strong> (grey cloud / DNS only).
              </p>
            </div>
          )}

          <div style={{
            background: '#f0fff4',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            border: '1px solid #c6f6d5',
            fontSize: '0.9rem',
            color: '#276749',
          }}>
            Once the DNS record is in place, your site will be live at <strong>https://{state.customDomain}</strong> — usually within a few minutes, sometimes up to an hour.
          </div>

          <button className="btn btn-secondary" onClick={handleRemoveDomain} disabled={saving} style={{ color: '#dc3545' }}>
            {saving ? 'Removing...' : 'Remove Domain'}
          </button>
        </div>
      )}

      {/* ───── Removing ───── */}
      {state.domainStatus === 'removing' && (
        <div className="card">
          <h2 style={{ marginBottom: '0.5rem' }}>Removing domain...</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            Cleaning up <strong>{state.customDomain}</strong>. This takes a couple of minutes.
            Your site will still be available at its topzite.net address.
          </p>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner" />
          </div>
        </div>
      )}

      {/* ───── Error ───── */}
      {state.domainStatus === 'error' && (
        <div className="card">
          <h2 style={{ marginBottom: '0.5rem', color: '#dc3545' }}>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            {state.message || 'We hit a problem setting up your domain. This sometimes happens — just try again and it usually works.'}
          </p>
          <button className="btn btn-primary" onClick={handleTryAgain} disabled={saving}>
            {saving ? 'Resetting...' : 'Try Again'}
          </button>
        </div>
      )}
    </div>
  );
}
